import { ForwardedRef, forwardRef, useId } from 'react';
import classNames from 'classnames';

import { V2, V2Types } from '@xemplo/popover';
import { List } from '@xemplo/stateful-list';

import { useTimePicker } from './hooks/use-timepicker';
import * as S from './time-picker.styles';
import { TimePickerProps } from './time-picker.types';

/**
 * TimePicker component that supports 12-hour and 24-hour formats.
 * It uses a popover to display a list of selectable times.
 * The input field is controlled, and the list is managed by the component.
 */
export const TimePicker = forwardRef(
  (props: TimePickerProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { onInputChange, onListChange } = props;
    const { rootProps, inputProps, timeList, handleBlur } = useTimePicker(props);
    const inputWrapperUniqueId = useId();

    return (
      <V2.Popover.Root {...rootProps} trigger={V2Types.PopoverTrigger.Focus}>
        <V2.Popover.Trigger usePropRefKey="wrapperRef">
          {({ open, setOpen }) => (
            <S.CustomXemploInput
              {...inputProps}
              wrapperId={inputWrapperUniqueId}
              type="text"
              ref={ref}
              className={classNames({ open })}
              onChange={(e) => {
                onInputChange?.(e);
                setOpen(false); // Close the popover on input change
              }}
              onBlur={handleBlur}
            />
          )}
        </V2.Popover.Trigger>
        <V2.Popover.Popup>
          {({ setOpen }) => (
            <List.SingleSelect
              items={timeList}
              triggerId={inputWrapperUniqueId}
              defaultSelected={props.value}
              onChange={(value) => {
                onListChange?.(value);
                setOpen(false); // Close the popover on selection
              }}
            />
          )}
        </V2.Popover.Popup>
      </V2.Popover.Root>
    );
  }
);

export default TimePicker;
